/**
 * Mattress Compatibility PDP block (snippets/product-block-mattress-compatibility.liquid).
 *
 * - Opens / closes the compatible mattresses drawer
 * - Adds a single mattress to the bag via /cart/add.js
 *   - PDP featured card: updates and opens the mini cart, as quick add does
 *   - Drawer card: updates the mini cart in the background and shows an
 *     "Item added to bag" notice in the drawer (the CTA does not change state)
 *
 * Tracking hooks — each interaction dispatches a bubbling CustomEvent from the block root
 * ([data-mattress-compat]) so tracking can listen on document:
 *
 *   mattress-compatibility:view            Section scrolled into view (once per page view)
 *   mattress-compatibility:more-click      'More Compatible Mattresses' clicked
 *   mattress-compatibility:drawer-open     Drawer opened
 *   mattress-compatibility:drawer-close    Drawer closed — detail.method: button | overlay | escape
 *   mattress-compatibility:product-click   Mattress PDP link clicked in the drawer
 *   mattress-compatibility:add-to-cart     Mattress added — detail.context: pdp | drawer
 *   mattress-compatibility:add-to-cart-error
 *
 * Every event's detail carries { size, collection, sourceProduct, mattresses } and, for
 * card interactions, { item, context } where item is read from the card's data-mc-* attrs.
 *
 * Example:
 *   document.addEventListener('mattress-compatibility:add-to-cart', function(e) {
 *     console.log(e.detail.item.sku, e.detail.context);
 *   });
 *
 * Analytics (MPBAU-1090, phase 1): the PDP / drawer Add to Bag, 'More Compatible Mattresses'
 * and drawer product links carry data-tracking-* attributes (section-tracking-click) that GTM
 * reads as sectionClick. Successful adds also fire the standard 'addToCart' ecommerce event
 * via mpApplyCartDrawerUpdate, as quick add does.
 */
(function() {
  var EVENT_PREFIX = 'mattress-compatibility:';
  var NOTICE_TIMEOUT = 4000;
  var ERROR_TEXT = 'Sorry, this item could not be added to your bag.';

  function MattressCompatibility(root) {
    this.root = root;
    this.drawer = root.querySelector('[data-mc-drawer]');
    this.overlay = root.querySelector('.mattress-compat-drawer__overlay');
    this.openButton = root.querySelector('[data-mc-open]');
    this.notice = this.drawer ? this.drawer.querySelector('[data-mc-notice]') : null;
    this.noticeTimer = null;
    this.lastFocus = null;

    this._moveDrawerToBody();
    this._bindEvents();
    this._observeView();
  }

  MattressCompatibility.prototype = {
    // The block renders inside the product form; keep the drawer outside it so its
    // buttons can never submit the form and fixed positioning is not clipped.
    _moveDrawerToBody: function() {
      if (!this.drawer) return;

      // Drop a copy left behind by a previous render (theme editor section reload).
      var self = this;
      document.querySelectorAll('body > [data-mc-drawer], body > .mattress-compat-drawer__overlay').forEach(function(el) {
        var id = el.id || el.getAttribute('data-drawer-id');
        if (id === self.drawer.id && el !== self.drawer && el !== self.overlay) el.remove();
      });

      if (this.overlay) document.body.appendChild(this.overlay);
      document.body.appendChild(this.drawer);
    },

    _bindEvents: function() {
      var self = this;

      if (this.openButton) {
        this.openButton.addEventListener('click', function(e) {
          e.preventDefault();
          self._emit('more-click');
          self.open();
        });
      }

      [this.root, this.drawer, this.overlay].forEach(function(scope) {
        if (!scope) return;

        scope.addEventListener('click', function(e) {
          var addButton = e.target.closest('[data-mc-add]');
          if (addButton) {
            e.preventDefault();
            self.addToCart(addButton);
            return;
          }

          var productLink = e.target.closest('[data-mc-product-link]');
          if (productLink) {
            self._emit('product-click', self._cardDetail(productLink));
            return;
          }

          var closeTrigger = e.target.closest('[data-mc-close]');
          if (closeTrigger) {
            e.preventDefault();
            self.close(closeTrigger === self.overlay ? 'overlay' : 'button');
          }
        });
      });

      document.addEventListener('keydown', function(e) {
        if (!self.isOpen()) return;

        if (e.key === 'Escape') {
          self.close('escape');
        } else if (e.key === 'Tab') {
          self._trapFocus(e);
        }
      });
    },

    // Keep Tab / Shift+Tab cycling inside the open drawer (it is aria-modal).
    _trapFocus: function(e) {
      var focusable = Array.prototype.filter.call(
        this.drawer.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex^="-"])'),
        function(el) {
          return !el.disabled && el.getAttribute('tabindex') !== '-1' && el.getClientRects().length > 0;
        }
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var inside = this.drawer.contains(document.activeElement);

      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    },

    _observeView: function() {
      var self = this;

      if (!('IntersectionObserver' in window)) {
        this._emit('view');
        return;
      }

      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            self._emit('view');
            observer.disconnect();
          }
        });
      }, { threshold: 0.5 });

      observer.observe(this.root);
    },

    isOpen: function() {
      return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
    },

    open: function() {
      if (!this.drawer || this.isOpen()) return;

      this.lastFocus = document.activeElement;
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-visible');
      if (this.openButton) this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-scroll');

      var closeButton = this.drawer.querySelector('.btn--close');
      if (closeButton) closeButton.focus();

      this._emit('drawer-open');
    },

    close: function(method) {
      if (!this.isOpen()) return;

      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      if (this.openButton) this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('no-scroll');
      this._hideNotice();

      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();

      this._emit('drawer-close', { method: method || 'button' });
    },

    addToCart: function(button) {
      var self = this;
      // AC: no visible state change on the CTA while adding; just block double adds.
      if (button.hasAttribute('data-mc-busy')) return;

      var detail = this._cardDetail(button);
      var variantId = button.getAttribute('data-variant-id');
      var formData = { items: [{ id: variantId, quantity: 1 }] };
      var addedItem = null;

      this._setBusy(button, true);

      // Match quick add: carry the gifting line property when the cart is being gifted.
      var giftCfg = (window.OsGlobals && window.OsGlobals.gifting) || {};
      var ready = giftCfg.enabled && typeof window.mpGiftingValueForVariant === 'function'
        ? fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              var giftVal = window.mpGiftingValueForVariant(cart, variantId);
              if (giftVal) {
                var props = {};
                props[giftCfg.property || '_gifting'] = giftVal;
                formData.items[0].properties = props;
              }
            })
            .catch(function() {})
        : Promise.resolve();

      ready
        .then(function() {
          return fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(formData)
          });
        })
        .then(function(r) {
          return r.json().then(function(data) {
            if (!r.ok || data.status) {
              throw new Error(data.description || 'cart/add failed');
            }
            return data;
          });
        })
        .then(function(data) {
          addedItem = data.items ? data.items[0] : data;
          return fetch('/cart?view=json&_dc=' + btoa(Date.now() + Math.random()));
        })
        .then(function(r) { return r.text(); })
        .then(function(text) {
          var cartData = JSON.parse(text);
          var fromDrawer = detail.context === 'drawer';

          if (typeof window.mpApplyCartDrawerUpdate === 'function') {
            window.mpApplyCartDrawerUpdate(cartData, {
              openDrawer: !fromDrawer,
              // Fires the standard addToCart ecommerce event, as quick add does.
              addedItem: addedItem,
              onComplete: function() { self._setBusy(button, false); }
            });
          } else {
            self._setBusy(button, false);
          }

          if (fromDrawer) {
            self._showNotice(self.root.getAttribute('data-mc-added-text'), false);
          }

          self._emit('add-to-cart', detail);
        })
        .catch(function(error) {
          console.error('Mattress compatibility add to bag error:', error);
          self._setBusy(button, false);

          if (detail.context === 'drawer') {
            self._showNotice(error.message || ERROR_TEXT, true);
          }

          detail.error = error.message;
          self._emit('add-to-cart-error', detail);
        });
    },

    _setBusy: function(button, busy) {
      if (busy) {
        button.setAttribute('data-mc-busy', '');
      } else {
        button.removeAttribute('data-mc-busy');
      }
    },

    _showNotice: function(message, isError) {
      if (!this.notice || !message) return;
      var notice = this.notice;

      clearTimeout(this.noticeTimer);
      notice.textContent = message;
      notice.classList.toggle('is-error', !!isError);
      notice.classList.remove('hide');

      // The notice sits at the top of the drawer; scroll up so it isn't missed further down the list.
      var scroller = this.drawer.querySelector('[data-scrollable]');
      if (scroller && scroller.scrollTop > 0) {
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scroller.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      this.noticeTimer = setTimeout(function() {
        notice.classList.add('hide');
      }, NOTICE_TIMEOUT);
    },

    _hideNotice: function() {
      if (!this.notice) return;
      clearTimeout(this.noticeTimer);
      this.notice.classList.add('hide');
    },

    _cardDetail: function(el) {
      var card = el.closest('[data-mc-card]');
      if (!card) return {};

      return {
        context: card.getAttribute('data-mc-context'),
        item: {
          position: parseInt(card.getAttribute('data-mc-position'), 10),
          productId: card.getAttribute('data-mc-product-id'),
          handle: card.getAttribute('data-mc-product-handle'),
          title: card.getAttribute('data-mc-product-title'),
          variantId: card.getAttribute('data-mc-variant-id'),
          sku: card.getAttribute('data-mc-sku'),
          price: parseFloat(card.getAttribute('data-mc-price')),
          range: card.getAttribute('data-mc-range'),
          preorder: card.getAttribute('data-mc-preorder') === 'true',
          mostPopular: card.getAttribute('data-mc-most-popular') === 'true'
        }
      };
    },

    _emit: function(name, extra) {
      var detail = {
        size: this.root.getAttribute('data-mc-size'),
        collection: this.root.getAttribute('data-mc-collection'),
        mattresses: parseInt(this.root.getAttribute('data-mc-count'), 10),
        sourceProduct: {
          id: this.root.getAttribute('data-mc-source-product-id'),
          title: this.root.getAttribute('data-mc-source-product-title')
        }
      };

      if (extra) {
        Object.keys(extra).forEach(function(key) { detail[key] = extra[key]; });
      }

      this.root.dispatchEvent(new CustomEvent(EVENT_PREFIX + name, { bubbles: true, detail: detail }));
    }
  };

  function init() {
    document.querySelectorAll('[data-mattress-compat]').forEach(function(root) {
      if (root.mattressCompatibility) return;
      root.mattressCompatibility = new MattressCompatibility(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the block is added / re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
